import React, { useRef, useState, useEffect } from 'react';
import DropdownList from 'atomic/dropdown-list';
import useOnClickOutside from 'hooks/use-onclick-outside';
import Icon from 'atomic/icon/icon';
import useToggle from 'hooks/use-toggle';
import classNames from 'classnames';
import { FieldError } from 'react-hook-form';

import './dropdown.scss';

interface DropdownProps {
    list: { label: string; value: string; isDisabled?: boolean; className?: string; }[];
    defaultValue?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    label?: string;
    hideSelectedItems?: boolean;
    disabled?: boolean;
    isTransparent?: boolean;
    isHideTitle?: boolean;
    icon?: string;
    error?: FieldError;
    iconColor?: 'primary' | 'secondary' | 'third' | 'fourth' | 'white' | 'dark' | 'gray' | 'danger';
}

const Dropdown: React.FC<DropdownProps> = ({
    list,
    defaultValue,
    onChange,
    placeholder,
    className,
    label,
    hideSelectedItems,
    isTransparent,
    isHideTitle,
    icon,
    iconColor,
    disabled,
    error
}) => {
    const ref = useRef(null);
    const [isOpen, toggleIsOpen] = useToggle();
    const [activeItemIndex, setActiveItemIndex] = useState(0);

    useEffect(() => {
        setActiveItemIndex(list.findIndex(item => item.value === defaultValue));
    }, [defaultValue]);

    useOnClickOutside(ref, () => toggleIsOpen.setToggleState(false));

    const onSelect = (value: string): void => {
        setActiveItemIndex(list.findIndex(item => item.value === value));
        toggleIsOpen.setToggleState(false);

        if (onChange) {
            onChange(value);
        }
    };

    return (
        <div ref={ref} className={classNames('dropdown', className)}>
            <button
                type="button"
                disabled={disabled}
                onClick={toggleIsOpen.toggle}
                className={classNames('dropdown__button', {
                    'dropdown__button--transparent': isTransparent,
                    'dropdown__button--disabled': disabled,
                    'dropdown__button--error': error
                })}
            >
                {label && (
                    <span
                        className={classNames('dropdown__label', {
                            'dropdown__label--transparent': isTransparent,
                            'dropdown__label--disabled': disabled,
                            'dropdown__label--error': error
                        })}
                    >
                        {label}
                    </span>
                )}
                {!isHideTitle && (
                    <span
                        className={classNames('dropdown__placeholder', {
                            'dropdown__placeholder--transparent': isTransparent,
                            'dropdown__placeholder--disabled': disabled,
                            'dropdown__placeholder--error': error
                        })}
                    >
                        {list[activeItemIndex]?.label || placeholder || 'Wybierz...'}
                    </span>
                )}
                <Icon
                    name={icon || (isOpen ? 'chevron-up' : 'chevron-down')}
                    color={iconColor}
                />
            </button>
            {isOpen && (
                <DropdownList
                    list={list}
                    selectedValue={list[activeItemIndex]?.value}
                    onSelect={onSelect}
                    hideSelectedItems={hideSelectedItems}
                />
            )}
            {error && (
                <span className="dropdown__error">
                    {error.message}
                </span>
            )}
        </div>
    );
};

export default Dropdown;
